import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Shield, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import Button from "../components/ui/Button";

const AdminSetup = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [secretKey, setSecretKey] = useState("");
  const [status, setStatus] = useState({ loading: false, success: false, error: null, data: null });
  const [ownerStatus, setOwnerStatus] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    // Check if user is owner
    const checkOwner = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/check-owner`,
          { credentials: "include" }
        );
        if (response.ok) {
          const data = await response.json();
          setOwnerStatus(data);
        }
      } catch (error) {
        console.error("Failed to check owner status:", error);
      }
    };
    
    if (user) {
      checkOwner();
    }
  }, [user]);

  const handleSetup = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null, data: null });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/setup/${secretKey}`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStatus({ loading: false, success: true, error: null, data });
      } else {
        const error = await response.json();
        setStatus({ loading: false, success: false, error: error.detail || "Setup failed", data: null });
      }
    } catch (error) {
      setStatus({ loading: false, success: false, error: "Network error", data: null });
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-6 bg-gradient-to-b from-background to-background/95">
      <div className="container mx-auto max-w-2xl">
        <div className="text-center mb-12">
          <Shield className="w-20 h-20 text-ruby mx-auto mb-6" />
          <h1 className="font-display text-4xl text-gold mb-4">Admin Setup</h1>
          <p className="text-muted-foreground">
            Owner-only endpoint per ottenere accesso admin
          </p>
        </div>

        {/* Owner Status */}
        {ownerStatus && (
          <div className={`mb-8 p-4 rounded-lg border ${
            ownerStatus.is_owner 
              ? "bg-gold/10 border-gold" 
              : "bg-ruby/10 border-ruby"
          }`}>
            <p className="text-sm">
              <strong>Email:</strong> {ownerStatus.email}
            </p>
            <p className="text-sm mt-2">
              <strong>Owner:</strong> {ownerStatus.is_owner ? "✅ Sì" : "❌ No"}
            </p>
            <p className="text-sm mt-2">
              <strong>Admin:</strong> {ownerStatus.is_admin ? "✅ Sì" : "❌ No"}
            </p>
            {ownerStatus.needs_admin_setup && (
              <p className="text-sm mt-4 text-gold">
                📝 Devi eseguire il setup admin usando la chiave segreta.
              </p>
            )}
          </div>
        )}

        {/* Setup Form */}
        {!status.success ? (
          <div className="rune-border bg-card/80 backdrop-blur rounded-lg p-8">
            <form onSubmit={handleSetup} className="space-y-6">
              <div>
                <label className="block text-sm font-heading text-foreground mb-2">
                  Chiave Segreta Admin
                </label>
                <input
                  type="text"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder="mytharion_secret_admin_2024"
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:border-gold focus:outline-none font-mono"
                  required
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Trova la chiave in <code className="text-gold">/app/backend/.env</code> → <code className="text-gold">ADMIN_SETUP_SECRET</code>
                </p>
              </div>

              {status.error && (
                <div className="p-4 bg-ruby/10 border border-ruby rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-ruby flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-ruby font-heading">Errore</p>
                    <p className="text-sm text-muted-foreground mt-1">{status.error}</p>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                variant="ruby"
                size="lg"
                className="w-full"
                disabled={status.loading || !secretKey}
              >
                {status.loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Setup in corso...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Ottieni Accesso Admin
                  </>
                )}
              </Button>
            </form>

            <div className="mt-8 p-4 bg-background/50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">ℹ️ Come funziona:</strong>
              </p>
              <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                <li>1. La tua email deve essere in <code>OWNER_EMAILS</code> nel file <code>.env</code></li>
                <li>2. Inserisci la chiave segreta da <code>ADMIN_SETUP_SECRET</code></li>
                <li>3. Il sistema ti promuoverà automaticamente ad admin</li>
                <li>4. Logout e re-login per vedere "Admin Panel" nel menu</li>
              </ul>
            </div>
          </div>
        ) : (
          /* Success State */
          <div className="text-center">
            <div className="w-24 h-24 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-gold" />
            </div>
            <h2 className="font-display text-3xl text-gold mb-4">Setup Completato!</h2>
            <p className="text-muted-foreground mb-8">
              Il tuo account è stato promosso ad <strong className="text-gold">Admin</strong>
            </p>

            {status.data?.next_steps && (
              <div className="rune-border bg-card/50 rounded-lg p-6 mb-8 text-left">
                <p className="font-heading text-sm text-foreground mb-4">📋 Prossimi Step:</p>
                <ol className="space-y-2">
                  {status.data.next_steps.map((step, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-gold">→</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="space-y-3">
              <Button
                onClick={() => window.location.href = "/"}
                variant="primary"
                size="lg"
              >
                Vai alla Home (Logout)
              </Button>
              <p className="text-xs text-muted-foreground">
                Dopo il re-login, troverai "Admin Panel" nel dropdown utente
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSetup;
